import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import useGrouping from 'Common/Utils/Hooks/useGrouping';
import errorCb from 'Common/Utils/errorCb';
import TemplateListView from './TemplateList.view';
import { fetchAmendments } from '../AmendContract.service';

function TemplateList({ type, year, onClose }) {
    const [dataSource, setDataSource] = useState();
    const [dataExportSource, setDataExportSource] = useState();
    const [dataExportAllSource, setDataExportAllSource] = useState();
    const [selectedYear, setSelectedYear] = useState(year || '');
    const prevYear = useRef(selectedYear);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedType, setSelectedType] = useState(type || null);
    const [allTypes, setAllTypes] = useState();
    const [showData, setShowData] = useState(false);
    const [goClicked, setGoClicked] = useState(!!(type && year));
    const grouping = useGrouping();
    const globalDispatch = useDispatch();
    document.title = `Contract Management - ${grouping} Amend Contract`;

    useEffect(() => {
        if (goClicked && selectedYear && selectedType) {
            setDataSource(() => getDataSource(selectedType, selectedYear, false, globalDispatch));
            setDataExportSource(() => getDataSource(selectedType, selectedYear, true, globalDispatch));
            setShowData(true);
        } else if (!goClicked) {
            setDataSource(null);
            setDataExportSource(null);
            setShowData(false);
        }
    }, [selectedYear, selectedType, goClicked, globalDispatch]);

    useEffect(() => {
        if (goClicked && selectedYear && allTypes) {
            setDataExportAllSource(() => getDataSource(allTypes, selectedYear, true, globalDispatch));
        } else if (!goClicked) {
            setDataExportAllSource(null);
        }
    }, [selectedYear, allTypes, goClicked, globalDispatch]);

    function handleAmendClick(template) {
        if (template) {
            const typeObj = allTypes.find(t => t.type === selectedType);
            const onBack = () => {
                const params = { year: selectedYear, type: selectedType };
                onClose('amend_contract', params);
            };
            onClose('amend_contract_detail', { template, type: typeObj, onBack });
        }
    }

    const handleFilterSubmit = useCallback(() => {
        if (selectedYear && selectedType) {
            setGoClicked(true);
        }
    }, [selectedYear, selectedType]);

    const handleSelectionChange = useCallback(value => {
        setSelectedTemplate(value);
        handleAmendClick(value);
    }, [selectedTemplate, allTypes]);

    const handleTypeChange = useCallback(value => {
        setSelectedType(value);
        setGoClicked(false);
        setSelectedTemplate(null);
    }, []);

    const handleYearChange = useCallback(value => {
        if (value !== prevYear.current) {
            prevYear.current = value;
            setSelectedYear(value);
            setSelectedType(null);
            setAllTypes(null);
            setGoClicked(false);
            setSelectedTemplate(null);
        }
    }, [prevYear]);

    return (
        <TemplateListView
            allowFilterSubmit={!!(selectedType && selectedYear)}
            amendEnabled={!!selectedTemplate}
            dataSource={dataSource}
            dataExportSource={dataExportSource}
            dataExportAllSource={dataExportAllSource}
            grouping={grouping}
            showData={showData}
            showExport={showData}
            selected={selectedTemplate}
            title={`${grouping} Amend Contract`}
            type={selectedType}
            year={selectedYear}
            onAmendClick={handleAmendClick}
            onFilterSubmit={handleFilterSubmit}
            onSelectionChange={handleSelectionChange}
            onTypeChange={handleTypeChange}
            onTypesLoaded={setAllTypes}
            onYearChange={handleYearChange}
        />
    );
}

function buildPagingQuery(forExport, page, pageSize) {
    const query = {
        sortOrder: 'asc',
        sortField: 'markingTeamTitle',
        page: forExport ? 0 : page,
        sizePerPage: forExport ? 0 : pageSize,
        searchText: '',
        flagExportData: forExport || false
    };
    return query;
}

function getDataSource(types, year, forExport, globalDispatch) {
    const typeCodes = Array.isArray(types) ? types.map(t => t.type) : [types];
    const errorCallback = errorCb(null, `Error fetching Contracts${forExport ? 'for export' : ''}.`, globalDispatch);

    return (page, pageSize) =>
        fetchAmendments(year, typeCodes, buildPagingQuery(forExport, page, pageSize), errorCallback);
}

TemplateList.propTypes = {
    type: PropTypes.string,
    year: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default TemplateList;